import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type LoadCarrierFormSchema, loadCarrierFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ModalLoadFailed } from "@/components/modals";
import { LoadCarrierForm } from "@/forms/load-carrier-form";
import { client } from "@/hono";

/** Props Interface */
interface EditLoadCarrierModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
	loadCarrierId: string;
}

export function EditLoadCarrierModal({
	onClose,
	isOpen,
	onComplete,
	loadCarrierId,
}: EditLoadCarrierModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.stock["load-carriers"][":loadCarrierId"].$get({
			param: { loadCarrierId },
		});
		const data = await responseHandler(response);
		return loadCarrierFormSchema.parse(data.loadCarrier);
	});
	const methods = useForm({
		resolver: resolver(loadCarrierFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: LoadCarrierFormSchema) {
		const response = await client.stock["load-carriers"][":loadCarrierId"].$patch({
			param: { loadCarrierId },
			json,
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.edit_load_carrier")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<LoadCarrierForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
